import { useEffect } from "react";

import { objectEntries } from "Libs/utils";
import {
  loadDeploymentCurrent,
  loadDeploymentNext,
  nextDeploymentSelector,
  currentDeploymentSelector,
  nextDeploymentErrorSelector,
  currentDeploymentErrorSelector
} from "Reducers/environment/deployment";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import type { Deployment } from "@packages/client";
import type {
  LoadDeploymentCurrentProps,
  LoadDeploymentNextProps
} from "Reducers/environment/deployment";

const addClassAndIcon = <T extends Record<string, { type: string }>>(
  service: T,
  kind: "routes" | "app" | "service" | "worker"
) =>
  objectEntries(service).reduce(
    (current, [key, value]) => {
      const icon = value?.type?.split(":")[0];
      current[key] = { name: key, icon, class: kind, kind, ...value };
      return current;
    },
    {} as Record<
      keyof T,
      {
        name: string | number | symbol;
        icon: string;
        class: string;
        kind: "routes" | "app" | "service" | "worker";
      } & T[keyof T]
    >
  );

export const getDeploymentServices = (
  deployment?: Deployment,
  includeRoutes = true
) => {
  if (!deployment) {
    return;
  }

  return Object.assign(
    includeRoutes
      ? { routes: addClassAndIcon(deployment.routes, "routes") }
      : {},
    addClassAndIcon(deployment.webapps, "app"),
    addClassAndIcon(deployment.services, "service"),
    addClassAndIcon(deployment.workers, "worker")
  );
};

/**
 * This hook utilizes the loadDeploymentCurrent thunk, supports a dispatch condition, and returns relevant deployment data
 * @param deployment
 * @param shouldDispatch
 * @returns
 */

export const useLoadDeploymentsCurrent = (
  deployment: LoadDeploymentCurrentProps,
  shouldDispatch: boolean | undefined = true
) => {
  const dispatch = useAppDispatch();
  const { organizationId, projectId, environmentId, settings } = deployment;

  const currentDeployment = useAppSelector(state =>
    currentDeploymentSelector(state, { ...deployment })
  );
  const currentDeploymentError = useAppSelector(state =>
    currentDeploymentErrorSelector(state, { ...deployment })
  );

  useEffect(() => {
    if (shouldDispatch) {
      dispatch(
        loadDeploymentCurrent({
          organizationId,
          projectId,
          environmentId,
          settings
        })
      );
    }
  }, [
    dispatch,
    organizationId,
    projectId,
    environmentId,
    shouldDispatch,
    settings
  ]);

  return {
    currentDeployment,
    currentDeploymentError
  };
};

/**
 * This hook utilizes the  loadDeploymentNext thunk, supports a dispatch condition, and returns relevant deployment data
 * @param deployment
 * @param shouldDispatch
 * @returns
 */

export const useLoadDeploymentsNext = (
  deployment: LoadDeploymentNextProps,
  shouldDispatch: boolean | undefined = true
) => {
  const dispatch = useAppDispatch();
  const { organizationId, projectId, environmentId } = deployment;

  const nextDeployment = useAppSelector(state =>
    nextDeploymentSelector(state, { ...deployment })
  );
  const nextDeploymentError = useAppSelector(state =>
    nextDeploymentErrorSelector(state, { ...deployment })
  );

  useEffect(() => {
    if (shouldDispatch) {
      dispatch(
        loadDeploymentNext({
          organizationId,
          projectId,
          environmentId
        })
      );
    }
  }, [dispatch, organizationId, projectId, environmentId, shouldDispatch]);

  return {
    nextDeployment,
    nextDeploymentError
  };
};
